import { trackExternalLinkOpened } from '@/utils/analytics';
import { openUrlSafely } from '@/utils/linking';
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { Stack, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useTranslation } from "react-i18next";
import {
    ScrollView,
    StyleSheet,
    Text,
    TouchableOpacity,
    View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

interface ResourceLinkProps {
  title: string;
  description?: string;
  url: string;
  icon?: string;
}

function ResourceLink({ title, description, url, icon }: ResourceLinkProps) {
  const { t } = useTranslation();
  const handlePress = () => {
    trackExternalLinkOpened(url);
    openUrlSafely(url, {
      title: t("common.linkErrorTitle"),
      message: t("common.linkErrorMessage"),
    });
  };

  return (
    <TouchableOpacity style={styles.resourceCard} onPress={handlePress}>
      <View style={styles.resourceIcon}>
        <Ionicons 
          name={(icon as any) || "link-outline"} 
          size={24} 
          color="#1C453F" 
        />
      </View>
      <View style={styles.resourceContent}>
        <Text style={styles.resourceTitle}>{title}</Text>
        {description && (
          <Text style={styles.resourceDescription}>{description}</Text>
        )}
      </View>
      <Ionicons name="open-outline" size={20} color="#CD7728" />
    </TouchableOpacity>
  );
}

export default function GoFurtherScreen() {
  const { t } = useTranslation();
  const router = useRouter();

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      <StatusBar style="dark" />
      <Stack.Screen options={{ headerShown: false }} />

      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 0.35]}
        style={styles.gradient}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => router.back()}
            style={styles.backButton}
          >
            <Ionicons name="chevron-back" size={24} color="#1C453F" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>{t("goFurther.headerTitle")}</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView
          contentContainerStyle={styles.content}
          showsVerticalScrollIndicator={false}
        >
          {/* Intro */}
          <Text style={styles.intro}>
            {t("goFurther.intro")}
          </Text>

          {/* Section: Consomm'action */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t("goFurther.consommactionSectionTitle")}</Text>
            <ResourceLink
              title={t("goFurther.consommactionPageTitle")}
              description={t("goFurther.consommactionPageDescription")}
              url="https://www.ge.ch/teaser/consomm-action"
              icon="leaf-outline"
            />
            <ResourceLink
              title={t("goFurther.eatSheetTitle")}
              description={t("goFurther.eatSheetDescription")}
              url="https://www.ge.ch/document/36365/annexe/0"
              icon="restaurant-outline"
            />
          </View>

          {/* Section: Vaisselle réutilisable */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t("goFurther.reusableSectionTitle")}</Text>
            <Text style={styles.sectionText}>
              {t("goFurther.reusableSectionText")}
            </Text>
            <ResourceLink
              title={t("goFurther.emportonsMalinTitle")}
              description={t("goFurther.emportonsMalinDescription")}
              url="https://www.ge.ch/teaser/emportons-malin#carte"
              icon="cafe-outline"
            />
          </View>

          {/* Section: S'engager */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              {t("goFurther.engageSectionTitle")}
            </Text>
            <ResourceLink
              title={t("goFurther.ecoDefisTitle")}
              description={t("goFurther.ecoDefisDescription")}
              url="https://www.ge.ch/dossier/durabilite-climat/climat/eco-defis"
              icon="flag-outline"
            />
            <ResourceLink
              title={t("goFurther.climateTitle")}
              description={t("goFurther.climateDescription")}
              url="https://geneve.nosgestesclimat.ch/"
              icon="calculator-outline"
            />
          </View>

          {/* Section: Dates à retenir */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t("goFurther.datesSectionTitle")}</Text>
            <ResourceLink
              title={t("goFurther.tasteWeekTitle")}
              description={t("goFurther.tasteWeekDescription")}
              url="https://www.gout.ch/"
              icon="calendar-outline"
            />
            <ResourceLink
              title={t("goFurther.foodWasteWeekTitle")}
              description={t("goFurther.foodWasteWeekDescription")}
              url="https://foodwaste.ch/fr/"
              icon="calendar-outline"
            />
          </View>

          {/* Contact */}
          <View style={styles.contactSection}>
            <Text style={styles.contactTitle}>{t("goFurther.questionTitle")}</Text>
            <TouchableOpacity
              style={styles.contactButton}
              onPress={() =>
                openUrlSafely("mailto:app@repaschallenge.ch", {
                  title: t("common.noEmailAppTitle"),
                  message: t("goFurther.noEmailAppMessage"),
                })
              }
            >
              <LinearGradient
                colors={["#1C453F", "#78A584"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={styles.contactGradient}
              >
                <Ionicons name="mail-outline" size={20} color="#FFF" />
                <Text style={styles.contactButtonText}>
                  {t("goFurther.contactButton")}
                </Text>
              </LinearGradient>
            </TouchableOpacity>
            <Text style={styles.contactEmail}>app@repaschallenge.ch</Text>
            <Text style={styles.contactEmail}>eco-defis@etat.ge.ch</Text>
          </View>

          <View style={{ height: 40 }} />
        </ScrollView>
      </LinearGradient>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FBEFE3",
  },
  gradient: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#FFF",
    alignItems: "center",
    justifyContent: "center",
  },
  headerTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 18,
    color: "#1C453F",
  },
  content: {
    paddingHorizontal: 24,
    paddingTop: 8,
  },
  intro: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    lineHeight: 22,
    marginBottom: 24,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#1C453F",
    marginBottom: 12,
  },
  sectionText: {
    fontFamily: "Inter_400Regular",
    fontSize: 14,
    color: "#666",
    lineHeight: 20,
    marginBottom: 12,
  },
  resourceCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFF",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
  },
  resourceIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F0F5F3",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  resourceContent: {
    flex: 1,
  },
  resourceTitle: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#000",
    marginBottom: 2,
  },
  resourceDescription: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#666",
    lineHeight: 16,
  },
  contactSection: {
    backgroundColor: "#FFF",
    borderRadius: 16,
    padding: 24,
    alignItems: "center",
    marginTop: 8,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  contactTitle: {
    fontFamily: "Inter_700Bold",
    fontSize: 16,
    color: "#000",
    marginBottom: 16,
  },
  contactButton: {
    borderRadius: 12,
    overflow: "hidden",
    width: "100%",
    marginBottom: 8,
  },
  contactGradient: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    gap: 8,
  },
  contactButtonText: {
    fontFamily: "Inter_700Bold",
    fontSize: 14,
    color: "#FFF",
  },
  contactEmail: {
    fontFamily: "Inter_400Regular",
    fontSize: 12,
    color: "#999",
  },
});
